import type { ReactNode } from 'react';
import {
  Box,
  Button,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import ErrorIcon from '@material-ui/icons/Error';
import StopIcon from '@material-ui/icons/Stop';
import RemoveIcon from '@material-ui/icons/Remove';
import AssessmentIcon from '@material-ui/icons/Assessment';
import type { ActivityLogPhase, IqeProjectStatus } from '../../interfaces/ui';
import {
  ACTIVITY_LOG_PHASES,
  toMinutesAndSeconds,
} from '../../utils/projectExecutionLifecycle';
import { ExecutionStepIcon } from './ExecutionStepper';
import ThreeDotIndicator from '../../common/ThreeDotIndicator';
import {
  isGenerateFailed,
  isProjectOperationStopped,
} from '../../utils/projectStatusChecks';
import '../../styles/projectDetails.css';

const normalizePhase = (phase: string) => phase.trim().toLowerCase();

const formatTimestamp = (timestamp: string) => {
  if (!timestamp) return '—';

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return timestamp;

  const parts = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find(part => part.type === type)?.value || '';

  return `${value('month')} ${value('day')}, ${value('year')} ${value(
    'hour',
  )}:${value('minute')} ${value('dayPeriod')}`;
};

interface ActivityLogTabProps {
  activityLog: ActivityLogPhase[];
  loading: boolean;
  error: string | null;
  onRefresh?: () => void;
  executionMode?: 'generate' | 'execute';
  actions?: ReactNode;
  emptyState?: ReactNode;
  showEmptyState?: boolean;
  isSubmitting?: boolean;
  projectStatus: IqeProjectStatus;
  isActivityComplete?: boolean;
}

export type ActivityPhaseStatus =
  | 'completed'
  | 'failed'
  | 'stopped'
  | 'skipped'
  | 'active'
  | 'pending';

export const normalizeActivityStatus = (status?: string) =>
  status?.trim().toLowerCase() || '';

export function getActivityPhaseStatus(
  latestStatus: string | undefined,
  phaseIndex: number,
  furthestPhaseIndex: number,
  stopBoundary: number,
  hasEntry: boolean,
  isStoppedRun: boolean,
  isActivityComplete = false,
): ActivityPhaseStatus {
  if (stopBoundary >= 0 && phaseIndex > stopBoundary) return 'skipped';

  if (isStoppedRun && phaseIndex === stopBoundary) return 'stopped';

  const normalizedStatus = normalizeActivityStatus(latestStatus);
  if (normalizedStatus === 'stop' || normalizedStatus === 'stopped')
    return 'stopped';
  if (!hasEntry) return 'pending';
  if (normalizedStatus === 'fail' || normalizedStatus === 'failed') {
    return 'failed';
  }
  return phaseIndex < furthestPhaseIndex || isActivityComplete
    ? 'completed'
    : 'active';
}

const getActivityStatusDisplay = (status: ActivityPhaseStatus) => {
  switch (status) {
    case 'completed':
      return {
        label: 'Completed',
        icon: <CheckCircleIcon />,
        tone: 'completed',
      };
    case 'failed':
      return { label: 'Failed', icon: <ErrorIcon />, tone: 'failed' };
    case 'stopped':
      return { label: 'Stopped', icon: <StopIcon />, tone: 'stopped' };
    case 'skipped':
      return { label: 'Skipped', icon: <RemoveIcon />, tone: 'skipped' };
    case 'active':
      return { label: 'In Progress', icon: null, tone: 'in-progress' };
    default:
      return {
        label: 'Pending',
        icon: <FiberManualRecordIcon />,
        tone: 'pending',
      };
  }
};

export default function ActivityLogTab({
  activityLog,
  loading,
  error,
  onRefresh,
  executionMode = 'generate',
  actions,
  showEmptyState = false,
  projectStatus,
  isActivityComplete = false,
}: ActivityLogTabProps) {
  const isMobile = useMediaQuery('(max-width: 1023px)');

  const visiblePhases =
    executionMode === 'execute'
      ? ACTIVITY_LOG_PHASES.filter(
          phase => phase.id === 'run' || phase.id === 'reporting',
        )
      : ACTIVITY_LOG_PHASES;

  const isGenerateFailedStatus = isGenerateFailed(projectStatus);
  const isStopped = isProjectOperationStopped(projectStatus);
  const displayActivityLog = activityLog.filter(
    entry => typeof entry.phase === 'string' && entry.phase.trim(),
  );

  const furthestPhaseIndex = displayActivityLog.reduce((furthest, entry) => {
    const index = visiblePhases.findIndex(
      phase => phase.id === normalizePhase(entry.phase),
    );
    return index > furthest ? index : furthest;
  }, -1);

  const stopBoundary = displayActivityLog.reduce((boundary, entry) => {
    const index = visiblePhases.findIndex(
      phase => phase.id === normalizePhase(entry.phase),
    );
    const status = normalizeActivityStatus(entry.status);
    return index >= 0 && (status === 'stop' || status === 'stopped')
      ? boundary < 0
        ? index
        : Math.min(boundary, index)
      : boundary;
  }, -1);

  const stoppedFailureBoundary = isStopped
    ? displayActivityLog.reduce((boundary, entry) => {
        const index = visiblePhases.findIndex(
          phase => phase.id === normalizePhase(entry.phase),
        );
        const status = normalizeActivityStatus(entry.status);
        const isStopEvent = ['fail', 'failed', 'stop', 'stopped'].includes(
          status,
        );
        return index >= 0 && isStopEvent && boundary < 0 ? index : boundary;
      }, -1)
    : -1;
  const effectiveStopBoundary = isStopped
    ? stoppedFailureBoundary >= 0
      ? stoppedFailureBoundary
      : furthestPhaseIndex
    : stopBoundary;

  const phaseGroups = visiblePhases.map((phase, index) => {
    const entries = displayActivityLog.filter(
      entry => normalizePhase(entry.phase) === phase.id,
    );
    const latestEntry = entries[entries.length - 1];
    const totalDuration = entries.reduce(
      (total, entry) => total + (Number(entry.durationSeconds) || 0),
      0,
    );

    const status = getActivityPhaseStatus(
      latestEntry?.status,
      index,
      furthestPhaseIndex,
      effectiveStopBoundary,
      Boolean(latestEntry),
      isStopped,
      isActivityComplete,
    );

    return { ...phase, entries, latestEntry, totalDuration, status };
  });

  return (
    <Box className="ec-tab-panel">
      {!showEmptyState && (
        <Box className="ec-activity-header">
          <Box className="ec-activity-header-actions">
            {actions}
            {onRefresh ? (
              <Button
                variant="contained"
                color="primary"
                className="ec-activity-refresh-btn"
                aria-label="Refresh activity log"
                disabled={loading}
                startIcon={
                  <RefreshIcon
                    className={
                      loading
                        ? 'ec-activity-refresh-icon ec-spin'
                        : 'ec-activity-refresh-icon'
                    }
                  />
                }
                onClick={onRefresh}
              >
                Refresh log
              </Button>
            ) : null}
          </Box>
        </Box>
      )}
      {error && <Typography className="ec-tab-error">{error}</Typography>}
      {showEmptyState && (
        <Box className="pd-empty-state">
          <Box className="pd-empty-icon-box">
            <AssessmentIcon />
          </Box>
          <Typography className="pd-empty-title">
            No test executions yet
          </Typography>
          <Typography className="pd-empty-subtitle">
            {isGenerateFailedStatus
              ? 'Test generation failed. Please retry by clicking the Generate & Execute button.'
              : isStopped
              ? 'This run was stopped. Test cases generated so far have been saved.'
              : 'Click Generate & Execute to generate your first run.'}
          </Typography>
        </Box>
      )}
      {!showEmptyState && loading && displayActivityLog.length === 0 && (
        <Box className="ec-activity-waiting">
          <ThreeDotIndicator />
          <Typography className="ec-activity-waiting-text">
            Agent is working in the background. Activity will appear here as it
            becomes available.
          </Typography>
        </Box>
      )}
      {displayActivityLog.length > 0 && (
        <Box className="ec-activity-log-layout">
          <Box
            className="ec-activity-log-header"
            style={{ display: isMobile ? 'none' : undefined }}
          >
            {['Phase', 'Status', 'Events', 'Start', 'End', 'Duration'].map(
              label => (
                <Typography
                  key={label}
                  component="span"
                  variant="caption"
                  className="ec-activity-log-header-label"
                >
                  {label}
                </Typography>
              ),
            )}
          </Box>
          {isMobile && (
            <Box>
              {phaseGroups.map(phase => {
                const statusDisplay = getActivityStatusDisplay(phase.status);
                const hasNoMetricsYet =
                  phase.status === 'active' &&
                  (phase.latestEntry?.events ?? 0) === 0 &&
                  !phase.entries[0]?.startTime &&
                  !phase.latestEntry?.endTime &&
                  phase.totalDuration === 0;

                return (
                  <Box
                    key={phase.id}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                      background: 'var(--iqe-bg-paper)',
                      border: '1px solid var(--iqe-divider)',
                      borderRadius: 8,
                      padding: 12,
                      marginBottom: 8,
                    }}
                  >
                    <Typography component="span" style={{ fontWeight: 600 }}>
                      {phase.label}
                    </Typography>
                    <Box
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        flexWrap: 'nowrap',
                      }}
                    >
                      <Typography
                        component="span"
                        variant="caption"
                        style={{
                          minWidth: 90,
                          color: 'grey',
                          fontSize: 13,
                          textAlign: 'left',
                        }}
                      >
                        Status
                      </Typography>
                      <Typography
                        component="span"
                        className={`ec-activity-status ec-activity-status-${statusDisplay.tone}`}
                        style={{
                          marginLeft: 8,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {statusDisplay.icon}
                        {statusDisplay.label}
                      </Typography>
                    </Box>
                    <Box
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        flexWrap: 'nowrap',
                      }}
                    >
                      <Typography
                        component="span"
                        variant="caption"
                        style={{
                          minWidth: 90,
                          color: 'grey',
                          fontSize: 13,
                          textAlign: 'left',
                        }}
                      >
                        Events
                      </Typography>
                      <Typography
                        component="div"
                        className="ec-activity-detail"
                        style={{
                          flex: 1,
                          marginLeft: 8,
                          textAlign: 'left',
                          fontSize: 14,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {hasNoMetricsYet
                          ? '—'
                          : phase.latestEntry?.events ?? ''}
                      </Typography>
                    </Box>
                    <Box
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        flexWrap: 'nowrap',
                      }}
                    >
                      <Typography
                        component="span"
                        variant="caption"
                        style={{
                          minWidth: 90,
                          color: 'grey',
                          fontSize: 13,
                          textAlign: 'left',
                        }}
                      >
                        Start
                      </Typography>
                      <Typography
                        component="div"
                        className="ec-activity-detail"
                        style={{
                          flex: 1,
                          marginLeft: 8,
                          textAlign: 'left',
                          fontSize: 14,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {phase.latestEntry
                          ? formatTimestamp(phase.entries[0]?.startTime || '')
                          : ''}
                      </Typography>
                    </Box>
                    <Box
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        flexWrap: 'nowrap',
                      }}
                    >
                      <Typography
                        component="span"
                        variant="caption"
                        style={{
                          minWidth: 90,
                          color: 'grey',
                          fontSize: 13,
                          textAlign: 'left',
                        }}
                      >
                        End
                      </Typography>
                      <Typography
                        component="div"
                        className="ec-activity-detail"
                        style={{
                          flex: 1,
                          marginLeft: 8,
                          textAlign: 'left',
                          fontSize: 14,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {phase.latestEntry
                          ? formatTimestamp(phase.latestEntry.endTime || '')
                          : ''}
                      </Typography>
                    </Box>
                    <Box
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        flexWrap: 'nowrap',
                      }}
                    >
                      <Typography
                        component="span"
                        variant="caption"
                        style={{
                          minWidth: 90,
                          color: 'grey',
                          fontSize: 13,
                          textAlign: 'left',
                        }}
                      >
                        Duration
                      </Typography>
                      <Typography
                        component="div"
                        className="ec-activity-detail"
                        style={{
                          flex: 1,
                          marginLeft: 8,
                          textAlign: 'left',
                          fontSize: 14,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {phase.latestEntry
                          ? hasNoMetricsYet
                            ? '—'
                            : toMinutesAndSeconds(phase.totalDuration)
                          : ''}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
          <Stepper
            orientation="vertical"
            activeStep={-1}
            className="ec-activity-stepper"
            style={{ display: isMobile ? 'none' : undefined }}
          >
            {phaseGroups.map(phase => {
              const statusDisplay = getActivityStatusDisplay(phase.status);

              const hasNoMetricsYet =
                phase.status === 'active' &&
                (phase.latestEntry?.events ?? 0) === 0 &&
                !phase.entries[0]?.startTime &&
                !phase.latestEntry?.endTime &&
                phase.totalDuration === 0;

              return (
                <Step
                  key={phase.id}
                  className={`ec-activity-step-${phase.status}`}
                  completed={phase.status === 'completed'}
                  active={phase.status === 'active'}
                >
                  <StepLabel
                    aria-label={phase.label}
                    StepIconComponent={() => (
                      <ExecutionStepIcon status={phase.status} />
                    )}
                  >
                    <Box
                      className={`ec-activity-phase-row ec-activity-phase-row-${phase.status}`}
                    >
                      <Box className="ec-activity-phase-row-content">
                        <Typography
                          component="span"
                          className="ec-activity-phase"
                        >
                          {phase.label}
                        </Typography>
                        <Typography
                          component="span"
                          className={`ec-activity-status ec-activity-status-${statusDisplay.tone}`}
                        >
                          {statusDisplay.icon}
                          {statusDisplay.label}
                        </Typography>
                        <Typography
                          component="span"
                          className="ec-activity-detail"
                        >
                          {hasNoMetricsYet
                            ? '—'
                            : phase.latestEntry?.events ?? ''}
                        </Typography>
                        <Typography
                          component="span"
                          className="ec-activity-detail"
                        >
                          {phase.latestEntry
                            ? formatTimestamp(phase.entries[0]?.startTime || '')
                            : ''}
                        </Typography>
                        <Typography
                          component="span"
                          className="ec-activity-detail"
                        >
                          {phase.latestEntry
                            ? formatTimestamp(phase.latestEntry.endTime || '')
                            : ''}
                        </Typography>
                        <Typography
                          component="span"
                          className="ec-activity-detail"
                        >
                          {phase.latestEntry
                            ? hasNoMetricsYet
                              ? '—'
                              : toMinutesAndSeconds(phase.totalDuration)
                            : ''}
                        </Typography>
                      </Box>
                    </Box>
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Box>
      )}
    </Box>
  );
}
